import { useMemo, useState, type ReactNode } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { Sparkle } from '@phosphor-icons/react';

import {
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  useAlertDialog,
} from '@clay-run/glaze/alert-dialog';
import { Badge } from '@clay-run/glaze/badge';
import { Button } from '@clay-run/glaze/button';
import { DebouncedSearchInput } from '@clay-run/glaze/debounced-search-input';
import { Icon } from '@clay-run/glaze/icon';
import { RadioGroupField } from '@clay-run/glaze/radio-group-field';
import { SegmentedControl, SegmentedControlOption } from '@clay-run/glaze/segmented-control';
import { StatusBadge } from '@clay-run/glaze/status-badge';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { FLOW_GONG_BROWSE_KEY, useApi } from '../../../_shared/hooks';
import type { GongFlowBrowseResponse, GongFlowSummary } from '../../../_shared/flow/gong';
import { CHANNELS } from '../../../_shared/launcher-copy-tokens';
import { getSequence, getSequenceUsage } from '../../api-client';
import { gongFlowDisplayName } from '../../gong-flow-name';
import {
  commitGongFlowRebind,
  createSequenceFromGongFlow,
  findLibrarySequenceForFlow,
  GongSequencePartialImportError,
  resolveGongFlowPick,
} from '../../gong-flow-picker-api';
import type { GongFlowPickOutcome } from '../../gong-flow-picker-api';
import { isVersionConflictError } from '../../conflict-errors';
import {
  adoptFreshPlayLadder,
  adoptUpdatePlaysResult,
  ensurePlayOwned,
  gateInPlayWrite,
} from '../../ensure-play-owned';
import { createSequence, updatePlays } from '../../mutations';
import { applyLadderSaveToCache } from '../../playbook-query-cache';
import { isArchivedInLibrary, isOwnedByPlay, ownedRowUseText, pickerRows, pickerTag } from '../../play-ownership';
import { libraryQueryKey, sequenceDetailQueryKey, sequenceUsageQueryKey } from '../../query-keys';
import { SAVE_GROUP_FIRST } from '../../sequence-card-menu';
import { distinctUnsentPlays, GROUP_MOVED_SENTENCE } from '../../usage-gate';
import { USAGE_SIDEBAR_RELOAD_FAILED_MESSAGE } from '../../usage-sidebar-reload';
import { confirmedFanOutSet, pluralize, saveMenuOptions, usageCards, usageHeadline } from '../../usage-sidebar-logic';
import type { PendingUsageSidebar, UsageSidebarOrigin } from '../../usage-sidebar-store';
import {
  draftRungMappingSequenceId,
  selectDraftRungs,
  setPlaybookDraftRungMapping,
  usePlaybookDraftStore,
} from '../../playbook-draft-store';
import { clearGeneratedCopy, clearGeneratedCopyForSequence } from '../../generated-copy-store';
import {
  clearCopyDrafts,
  clearCopyDraftsForSequence,
  sequenceHasAnyCopyDrafts,
  sequenceHasCopyDrafts,
  useSequenceCopyDraftStore,
} from '../../sequence-copy-draft-store';
import { channelBadgeColor } from '../../subtype-badge-colors';
import type {
  LauncherChannel,
  PlaybookRung,
  PlaybooksLibraryResponse,
  PlaybooksSaveSequenceStepsResponse,
  PlaybooksSequenceSummary,
  PlaybooksUsageResponse,
  SequenceMapping,
} from '../../schemas';
import { LauncherGongFlowBrowse } from '../LauncherGongFlowBrowse';
import { PushDrawer } from '../PushDrawer';
import { SaveMenu } from '../SaveMenu';
import { SequenceCreateFields } from '../SequenceCreateFields';
import { CopyDiffView } from './CopyDiffView';

/**
 * Round-3b QA fixes, G3 — the launcher's own `GongFlowPickerInline` (and its
 * private `BROWSE_QUERY_KEY`) is gone; the Gong tab now opens
 * `LauncherGongFlowBrowse`, which is driven by Flow's real
 * `useGongFlowBrowseController()` and therefore caches under Flow's OWN key,
 * `FLOW_GONG_BROWSE_KEY` (re-exported via `_shared/hooks`). This reads that
 * SAME key rather than keeping a second, now-dead query alive under the old
 * literal — one catalog fetch, not two (decision: reconcile onto Flow's key,
 * per the plan's "prefer the former" guidance). `queryClient.getQueryData`
 * rather than a second `useQuery` here: the browse panel is always opened
 * before a row click can happen at all, so the cache is warm by the time
 * `resolvedFlowName` below needs it.
 */

/**
 * Launcher V2 PR C / QA-fixes E2b+E2c — assign (or re-assign) a rung's
 * SEQUENCE, in a right-side push panel.
 *
 * Codex adversarial review (PR C): `setPlaybookDraftRungMapping` had no
 * production caller at all. `addDraftRung` stages a new rung with
 * `mapping: null`, nothing in the add-rung flow ever set one, and
 * `SequencesStep` only rendered routing UI for `playbookQuery.data.head.rungs`
 * — so a rung the rep had just added was invisible there. This panel is that
 * path; `SequencesStep` now renders the rail from the DRAFT, so unsaved rungs
 * reach it too.
 *
 * ── E2b — REHOSTED AS A PUSH DRAWER, NOT A MODAL (plan Decision 8) ────────
 * Originally `SequenceMappingModal`, a `Modal`. The prototype's `sideSeqPicker`
 * is a `.side` PUSH panel — this file now builds on `PushDrawer.tsx`, so this
 * picker and every other A2 side surface share one dismissal/layout contract.
 * Renamed to `SequenceMappingDrawer` throughout. (The other consumer of that
 * primitive, `RungRosterDrawer`, was deleted by C3 — the member-preview table
 * replaced it; this drawer is a DIFFERENT screen and is unaffected.)
 *
 * Also per the prototype's `sideSeqPicker`: a channel filter (segmented, "All
 * channels" + one per `CHANNELS` entry), a search box, a "Create a new
 * Sequence" doorway, and per-row metadata (channel/version/step-count/usage
 * badges + a first-step subject preview) in place of the old flat
 * `RadioGroupField` option list.
 *
 * Round-3-QA-fixes plan (U4, decision 4) — the doorway used to be a small,
 * file-local `CreateSequenceFields` that deliberately excluded Gong Engage
 * (D3, live-QA round 2, Batch 5) rather than importing
 * `PlaybooksConfigPage.tsx`'s own (then-private) `SequenceCreateFields`; the
 * stated reason ("that file isn't part of this change's touched-file set")
 * no longer holds once both surfaces are in scope together, so that private
 * form was LIFTED into a shared `../SequenceCreateFields` and this doorway
 * now renders it directly — offering all three channels, with Gong requiring
 * a real flow pick exactly like Config → Sequences, superseding D3. Non-Gong
 * channels still call the plain `createSequence` mutation; Gong branches to
 * `createSequenceFromGongFlow` with the same `GongSequencePartialImportError`
 * handling Config uses (see `handleCreateSequence` below).
 *
 * Per-row "N playbooks"/first-step-preview metadata needs each candidate
 * sequence's HEAD content, which the library summary
 * (`PlaybooksSequenceSummary`) doesn't carry — fetched here under the SAME
 * query key `SequencesStep`/`confirmSwap` already use
 * (`sequenceDetailQueryKey(id)`), so a sequence already loaded elsewhere
 * resolves from cache, and whichever one a rep picks next is already warm.
 *
 * ── E2c — SENDER IS NO LONGER COLLECTED HERE (plan Decision 10) ──────────
 * `SequenceMappingSchema.sender` was required-non-empty, which is why this
 * picker used to collect one (via `SenderChoiceFields`) alongside the
 * sequence itself. Spencer's call: sender belongs where the rest of a
 * sequence's setup lives — `InlineSenderPopover`, already rendered inline
 * next to `SequencesStep`'s step editors and `SamplePreviewCard` — not in
 * this picker. `SequenceMappingSchema.sender` is now a plain (possibly empty)
 * string, so `handleSave` stages `sender: currentMapping?.sender ?? ''`:
 * preserved across a sequence swap (it's a per-mapping setting, not a
 * per-sequence one — same reasoning as `bookingLinkIdentity`/`cc`/`replyTo`
 * below), empty for a brand-new mapping. `sequence-readiness.ts` already
 * treats an empty sender as `'not_ready'`, and `SequencesStep`'s
 * `senderLabel`/`SamplePreviewCard` already degrade an empty sender
 * gracefully (see that file's own E2c notes) — nothing here has to guess a
 * default.
 *
 * ── WRITE-THROUGH (2026-09-02 rip-out, U4) ────────────────────────────────
 * A mapping change persists immediately via `setPlaybookDraftRungMapping`
 * (`playbook-draft-store.ts#persistLadder`) — there is no more "staged,
 * saved later on Review" state. Addressed by `draftKey`, so this works
 * identically for a persisted rung (`rungDraftKey(rung.id)`) and one added
 * this session (still mid-flight, no server id yet).
 *
 * ── E2a — DESTRUCTIVE-CONFIRM DIFF ON A SEQUENCE SWAP ──────────────────────
 * Re-pointing a rung that is ALREADY mapped onto a DIFFERENT sequence loses
 * that rung's prior association — and, because copy drafts are keyed by THIS
 * rung's `draftKey` plus sequence and position (`sequence-copy-draft-store.ts`,
 * D1 shared-edit-leak fix), any unsaved typing for the old mapping becomes
 * unreachable once this rung no longer maps that sequence — there is no other
 * rail card that could show it, even if another rung happens to map the same
 * sequence. `handleSave` stages nothing for that
 * case until the rep confirms a danger-styled `AlertDialog` that renders the two
 * sequences' head steps side by side, via the same `CopyDiffView`/`copyDiffRows`
 * the AI-copy-generation review already uses for an old-vs-candidate diff
 * (`CopyDiffView.tsx`'s own "E2a REUSE" doc). Re-confirming the SAME sequence
 * (no actual change) or mapping a previously-unmapped rung has nothing to
 * lose and stages immediately, exactly as before this unit.
 *
 * The dialog is built and opened with data already in hand (both sequences'
 * head steps fetched first) rather than opened eagerly and filled in later:
 * `useAlertDialog`'s `content` is a JSX snapshot taken at the `openAlertDialog`
 * call (`DialogBase.tsx` stores it as plain state), not a live-rendering
 * reference back to this component, so anything the dialog needs to show must
 * already be resolved before that call. It is confirm-or-cancel immediately —
 * closing it (either button, or the header's own close) is the only way out,
 * and nothing here holds it open across an unmount — so `CopyDiffView`'s
 * documented stale-snapshot caveat about a review surviving a rung switch does
 * not apply to this use of it.
 */

/** Edge (c) — the swap confirm's extra sentence when a group leaves its copy for the copy's own Library row. */
const SWAP_BACK_TO_LIBRARY_SENTENCE =
  "This group will follow the Library version again. Your edited version stays in the picker as 'Not used by any group yet'.";

const CHANNEL_LABEL_BY_VALUE: Record<string, string> = Object.fromEntries(
  CHANNELS.map((c) => [c.value, c.label]),
);

function channelLabel(channel: string): string {
  return CHANNEL_LABEL_BY_VALUE[channel] ?? channel;
}

/** "All channels" + one segment per `CHANNELS` entry — the prototype's `sideSeqPicker` filter. */
const CHANNEL_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All channels' },
  ...CHANNELS,
];

export interface SequenceMappingDrawerProps {
  /** The playbook this rung belongs to — `setPlaybookDraftRungMapping`'s write-through save target. */
  playbookId: string;
  /**
   * Plan 2026-09-25-002, U6 (Decision 13) — the hosting play, or `undefined`
   * in the playbook doorway. A sequence created here inside a play is the
   * play's own (`owningPlayId`), and the ladder cache patch targets the
   * play-scoped playbook key. The mapping save itself needs nothing extra:
   * the draft store already writes the play's ladder.
   */
  playId?: string;
  /** The draft rung being mapped — `rungDraftKey(rung.id)` or an unsaved rung's own `draftKey`. */
  draftKey: string;
  /**
   * Plan 2026-09-28-006 U6 (rulebook R3) — the group's server id, or `null`
   * for a group staged this session and not saved yet. Every in-play copy
   * this drawer makes (a Gong rebind or first bind) is made for THIS group
   * only; an unsaved group can't be copied for ("Save this group first").
   */
  rungId: string | null;
  /** Human-readable name of the segmentation this rung routes, for the drawer header. */
  rungName: string;
  /** The rung's currently staged-or-persisted mapping, or `null` when unmapped. */
  currentMapping: SequenceMapping | null;
  /**
   * The library's sequences — `GET /v2/library`'s own list, passed down from
   * `SequencesStep`'s existing query rather than fetched again here (the whole
   * step already has it in hand). `undefined` while that query is in flight.
   */
  sequences: readonly PlaybooksSequenceSummary[] | undefined;
  /**
   * Live-QA round 2 (C2, wired for real by D2) — seeds the channel-segmented
   * filter below so a caller can land the drawer directly on one channel
   * (e.g. `SequencesStep`'s "Choose a Gong flow" / "Change flow" doorways
   * passing `'gong_engage'`). `undefined` (the default) leaves the filter on
   * "All channels", matching the plain "Change sequence" entry point.
   * `'gong_engage'` lands directly on the Gong tab — a "Browse Gong flows"
   * button opening `LauncherGongFlowBrowse` here, replacing the create
   * doorway (D2, Batch 6; round-3b QA fixes G3).
   */
  initialChannelFilter?: LauncherChannel;
  onClose: () => void;
}

export function SequenceMappingDrawer({
  playbookId,
  playId,
  draftKey,
  rungId,
  rungName,
  currentMapping,
  sequences,
  initialChannelFilter,
  onClose,
}: SequenceMappingDrawerProps) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { openAlertDialog, closeAlertDialog } = useAlertDialog();
  const [sequenceId, setSequenceId] = useState<string | null>(currentMapping?.sequenceId ?? null);
  const [showErrors, setShowErrors] = useState(false);
  /** E2a — fetching both sequences' head steps to build the swap-confirm diff. */
  const [isCheckingSwap, setIsCheckingSwap] = useState(false);
  /** E2b — the channel-segmented + free-text filter over the row list below. */
  const [channelFilter, setChannelFilter] = useState<string>(initialChannelFilter ?? 'all');
  const [search, setSearch] = useState('');
  /** E2b — the "Create a new Sequence" doorway swaps the row list for a create form. */
  const [isCreatingSequence, setIsCreatingSequence] = useState(false);
  const [isSavingNewSequence, setIsSavingNewSequence] = useState(false);
  /** D2 (Batch 6) — a Gong flow row click's dispatch is in flight. */
  const [isPickingFlow, setIsPickingFlow] = useState(false);
  /** Round-3b QA fixes, G3 — whether Flow's real Gong browse panel is open. */
  const [gongBrowseOpen, setGongBrowseOpen] = useState(false);
  /**
   * R5-11 — the Gong tab's "Browse Gong flows" doorway (`handleBrowseGongFlowPick`,
   * below), when the picked flow has no existing library sequence, opens the
   * create form (screen 3) pre-filled with this flow instead of silently
   * creating a null-sender sequence. `undefined` is the default, un-prefilled
   * state — including for the plain "Create a new Sequence" doorway, which
   * always clears this first.
   */
  const [gongCreatePrefillFlow, setGongCreatePrefillFlow] = useState<GongFlowSummary | undefined>(undefined);

  const sequenceError = sequenceId === null ? 'Pick a sequence.' : null;

  /**
   * Plan 2026-09-28-006 U6 (rulebook R11, R18) — the rows this picker
   * renders: every Library row AND each of this play's own copies as its own
   * row (`pickerRows`). The rep gets exactly the row they click — a Library
   * row maps the Library id, a copy row maps the copy; nothing is translated
   * or substituted. Archived-in-Library rows are never choosable. Outside a
   * play (`playId === undefined`) the list is the unscoped Library, unchanged.
   */
  const pickerSequences = useMemo(
    () => (sequences === undefined ? undefined : playId === undefined ? sequences : pickerRows(sequences, playId)),
    [sequences, playId],
  );

  /**
   * [R19] The groups of THIS play (the draft ladder the step behind this
   * drawer is editing) and their segment names — {@link ownedRowUseText}'s
   * "Used by <group>" line under each copy row, so two same-named rows (the
   * Library piece and this play's copy) can be told apart. Segment names come
   * from the same play-scoped library entry `sequences` was read from.
   */
  const draftRungs = usePlaybookDraftStore(selectDraftRungs);
  const ladderRungs = useMemo<PlaybookRung[]>(
    () =>
      draftRungs.map((rung, index) => ({
        ...rung,
        id: rung.id ?? rung.draftKey,
        position: index,
      })),
    [draftRungs],
  );
  const segmentationNameById = useMemo(() => {
    const library = queryClient.getQueryData<PlaybooksLibraryResponse>(libraryQueryKey(playId));
    return new Map((library?.segmentations ?? []).map((segmentation) => [segmentation.id, segmentation.name]));
    // `sequences` is the same library entry's list — a fresh library read re-derives the names.
  }, [queryClient, playId, sequences]);

  /** [R18] The group's current row when an admin archived it in the Library — `undefined` otherwise. */
  const currentArchivedRow = useMemo(() => {
    if (playId === undefined || currentMapping === null) return undefined;
    const row = sequences?.find((sequence) => sequence.id === currentMapping.sequenceId);
    return row !== undefined && isArchivedInLibrary(row) ? row : undefined;
  }, [playId, currentMapping, sequences]);

  const filteredSequences = useMemo(() => {
    if (!pickerSequences) return undefined;
    const q = search.trim().toLowerCase();
    return pickerSequences.filter(
      (sequence) =>
        (channelFilter === 'all' || sequence.channel === channelFilter) &&
        (q === '' || sequence.name.toLowerCase().includes(q)),
    );
  }, [pickerSequences, channelFilter, search]);

  /**
   * E2b — see this file's module doc: each visible row's version-usage badge
   * and first-step preview need the sequence's HEAD, not just its library
   * summary. One query per currently-filtered row, same query key the rest of
   * this step already fetches sequence detail under, so cache is shared both
   * ways.
   */
  const detailQueries = useQueries({
    queries: (filteredSequences ?? []).map((sequence) => ({
      queryKey: sequenceDetailQueryKey(sequence.id),
      queryFn: () => getSequence(apiFetch, sequence.id),
    })),
  });

  /**
   * E2a — fetches both sequences' head steps, then opens the danger-styled
   * confirm with the diff already resolved (see this file's module doc for
   * why the fetch must finish BEFORE `openAlertDialog`). `getSequence` is
   * routed through `queryClient.fetchQuery` under the SAME query key
   * `SequencesStep` uses, so the currently-mapped sequence — already loaded
   * for the editor shell behind this drawer — resolves from cache rather than
   * a second network round trip.
   */
  async function confirmSwap(fromSequenceId: string, selected: PlaybooksSequenceSummary, mapping: SequenceMapping) {
    setIsCheckingSwap(true);
    try {
      const [fromDetail, toDetail] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: sequenceDetailQueryKey(fromSequenceId),
          queryFn: () => getSequence(apiFetch, fromSequenceId),
        }),
        queryClient.fetchQuery({
          queryKey: sequenceDetailQueryKey(selected.id),
          queryFn: () => getSequence(apiFetch, selected.id),
        }),
      ]);
      const fromName = sequences?.find((s) => s.id === fromSequenceId)?.name ?? fromDetail.sequence.name;
      // D1 shared-edit-leak fix — scoped by THIS rung's own `draftKey`, so the
      // warning fires only when THIS rung has unsaved drafts, matching what
      // the copy below already claims ("the current mapping"), not every rung
      // that happens to map the same sequence.
      const hasUnsavedDrafts = sequenceHasCopyDrafts(
        useSequenceCopyDraftStore.getState(),
        draftKey,
        fromSequenceId,
      );
      const lossClause = hasUnsavedDrafts
        ? ' and any unsaved copy drafts for the current mapping'
        : '';
      // Plan 2026-09-28-006 U6 (rulebook R11/R18, edge (c)) — a group moving
      // off THIS play's copy back onto the Library row it came from follows
      // the Library again; the copy is not deleted, it stays a picker row. A
      // swap between two Library rows (or onto any other row) says nothing
      // extra.
      const fromRow = sequences?.find((s) => s.id === fromSequenceId);
      const isSwapBackToLibrary =
        playId !== undefined &&
        fromRow !== undefined &&
        fromRow.owningPlayId === playId &&
        fromRow.forkSourceId !== null &&
        selected.id === fromRow.forkSourceId;
      const swapBackClause = isSwapBackToLibrary ? ` ${SWAP_BACK_TO_LIBRARY_SENTENCE}` : '';
      const descriptionText =
        `${rungName} currently uses "${fromName}". Switching it to "${selected.name}" ` +
        `drops that association${lossClause} — this can't be undone.${swapBackClause}`;
      openAlertDialog({
        size: 'lg',
        // R5-11 — widened to 680px for the side-by-side diff below; Tailwind
        // v4 important-variant (`!`) needed because Glaze's `AlertDialog`
        // combines the `size` token and this `className` via plain `clsx`
        // (not tailwind-merge), so without it the `lg` (512px) size token
        // would win over this wider max-width.
        className: 'sm:max-w-[680px]!',
        content: (
          <>
            <AlertDialogHeader onClose={closeAlertDialog}>
              <AlertDialogTitle>
                Replace &quot;{fromName}&quot; with &quot;{selected.name}&quot;?
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody className="flex flex-col gap-4">
              <AlertDialogDescription>{descriptionText}</AlertDialogDescription>
              <CopyDiffView
                headSteps={fromDetail.head?.steps ?? []}
                generatedSteps={toDetail.head?.steps ?? []}
                headChannel={fromDetail.sequence.channel}
                generatedChannel={toDetail.sequence.channel}
                isHeadStale={false}
                leftColumnLabel={fromName}
                rightColumnLabel={selected.name}
              />
            </AlertDialogBody>
            <AlertDialogActions>
              <Button variant="secondary" onClick={closeAlertDialog}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  // Write-through (2026-09-02 rip-out) — persists immediately
                  // rather than staging for a later Review-step save; errors
                  // surface as a toast rather than blocking this dialog's own
                  // dismissal, matching every other write-through call site
                  // (`AddSegmentationDrawerBody#addAndPersist`).
                  //
                  // ── Bug 4 fix (Codex adversarial review, 2026-09-02) ──────
                  // `clearCopyDrafts` used to run HERE, synchronously and
                  // unconditionally, before the save above had even started —
                  // so a FAILED save (network error, 409, anything the
                  // `.catch` below shows a toast for) still permanently
                  // destroyed the rep's in-progress copy edits on the OLD
                  // mapping, even though the swap itself never took effect.
                  // This dialog's own copy promises the old association "and
                  // any unsaved copy drafts for the current mapping" are
                  // dropped when the swap happens — moving the clear into the
                  // success branch of the SAME promise is what makes that
                  // promise true in both outcomes: nothing is dropped unless
                  // the mapping change actually landed. Codex adversarial-
                  // review Issue 3's fix (dropping the OLD sequence's drafts
                  // at all, restated below) is preserved — only the timing
                  // changed.
                  //
                  // Round-2-fixes M2 addition — the SAME "landed" guard
                  // `SequencesStep.tsx#handleRemoveMapping` uses: a bare
                  // `response` truthiness check treats a 409-retry no-op (the
                  // fresh head already carries this exact swap —
                  // `persistLadderOnce` returns `null` in that case) as a
                  // failure and skips this cleanup, even though the swap
                  // genuinely landed. The staged Generate-with-AI review for
                  // the OLD sequence is dropped on this same success branch,
                  // for the same reason the copy draft is (Codex adversarial
                  // review round 2, Might-Break #2) — otherwise it reappears
                  // the instant this rung is swapped back onto that old
                  // sequence.
                  //
                  // Round-3 (Codex MUST-ADDRESS) — same split as
                  // `SequencesStep.tsx#handleRemoveMapping` (see that doc
                  // comment for the full explanation): `landed` alone no
                  // longer gates the module-scope store cleanup
                  // (`clearCopyDrafts`/`clearGeneratedCopy`), only whether
                  // `reflectedHere` below says THIS flow's live draft still
                  // reflects the swap that was requested. A rep who has since
                  // navigated to a different playbook (even a duplicate
                  // retaining this same rung id) has a draft whose
                  // `playbookId` no longer matches, so `reflectedHere` reads
                  // `false` and that other flow's staged review survives. The
                  // two cache invalidations and `applyLadderSaveToCache`
                  // below keep the `landed` guard unchanged — they are
                  // id-keyed and stay correct for playbook A regardless of
                  // where the rep is now.
                  //
                  // F2 fix — `reflectedHere` used to compare the live draft's
                  // `mapping.sequenceId` against `selected.id` (what the rep
                  // picked from the Library). A play-mode save can FORK
                  // `selected.id` into a new play-owned copy, so the rung's
                  // ADOPTED mapping ends up pointing at neither `selected.id`
                  // nor `fromSequenceId` — and the old `=== selected.id`
                  // check read `false` for that legitimately successful
                  // forked swap, silently skipping the copy-draft cleanup
                  // below. Comparing against the FORK SOURCE instead ("is
                  // this rung no longer pointing at the OLD sequence") is
                  // fork-agnostic and correct either way.
                  void setPlaybookDraftRungMapping(apiFetch, playbookId, draftKey, mapping)
                    .then((response) => {
                      const currentSequenceId = draftRungMappingSequenceId(playbookId, draftKey);
                      const reflectedHere = currentSequenceId !== undefined && currentSequenceId !== fromSequenceId;
                      const landed = response !== null || reflectedHere;
                      if (!landed) return undefined;
                      if (reflectedHere) {
                        // Codex adversarial-review fix (Issue 3, 2026-09-02) —
                        // this dialog's own copy claims the old mapping's
                        // unsaved copy drafts are dropped "and this can't be
                        // undone". Until that fix, nothing here actually
                        // dropped them: they stayed in
                        // `sequence-copy-draft-store.ts` under
                        // `${draftKey}|${fromSequenceId}|position`, dormant,
                        // and a later re-map of THIS rung back onto the SAME
                        // old sequence would resurrect them — contradicting
                        // the promise above.
                        clearCopyDrafts(draftKey, fromSequenceId);
                        clearGeneratedCopy(draftKey, fromSequenceId);
                      }
                      // Root cause (b), belt-and-suspenders (round-3-QA-fixes
                      // plan, Unit C3) — a swap changes BOTH sequences'
                      // usage: the old one loses a place, the new one gains
                      // one. Neither's detail cache was invalidated before
                      // this fix.
                      void queryClient.invalidateQueries({
                        queryKey: sequenceDetailQueryKey(fromSequenceId),
                      });
                      void queryClient.invalidateQueries({
                        queryKey: sequenceDetailQueryKey(selected.id),
                      });
                      return response
                        ? applyLadderSaveToCache(queryClient, playbookId, response, playId)
                        : undefined;
                    })
                    .catch((error) => {
                      addErrorToast({
                        content:
                          error instanceof Error ? error.message : 'Could not update this mapping — try again.',
                      });
                    });
                  closeAlertDialog();
                  onClose();
                }}
              >
                Replace sequence
              </Button>
            </AlertDialogActions>
          </>
        ),
      });
    } catch (error) {
      addErrorToast({
        content:
          error instanceof Error
            ? error.message
            : 'Could not load these Sequences to compare — try again.',
      });
    } finally {
      setIsCheckingSwap(false);
    }
  }

  /**
   * R5-11 — the write-through half of `handleSave`, extracted so a Gong
   * create/pick that RESOLVES a `PlaybooksSequenceSummary` directly (the new
   * doorways below) can commit immediately instead of only checking a radio
   * back on screen 1 and waiting for a second, manual "Save sequence" click.
   * Nothing about the write itself changed — this is a pure lift, not a
   * rewrite (mapping build, the E2a swap-confirm branch, and the plain
   * write-through call are all byte-for-byte what `handleSave` used to do
   * inline).
   */
  function commitSelection(selected: PlaybooksSequenceSummary) {
    const mapping: SequenceMapping = {
      // Spread first so the mapping's OTHER fields (`cc`, `replyTo`,
      // `bookingLinkIdentity`) survive a sequence swap — they are per-mapping
      // settings, not per-sequence ones, and silently dropping them here would
      // be a data loss the rep never asked for.
      ...(currentMapping ?? {}),
      sequenceId: selected.id,
      // No version pin to preserve — a mapping always renders its sequence's
      // current head (the pin was removed entirely, 2026-09-02 rip-out — see
      // plans/2026-09-02-001-launcher-v2-rip-out-versioning-plan.md).
      // The channel is the SEQUENCE's, never a separate choice: `createVersion`
      // verifies a mapping's channel matches the referenced sequence identity's.
      channel: selected.channel,
      // Playbooks QA round 4, U9 (SN1) / Decision 3 — COPY-ON-MAP. Sender is
      // no longer a per-mapping setting the rep edits on the rung: it belongs
      // to the SEQUENCE's identity, so mapping a sequence copies its
      // `default_sender` onto `mapping.sender` here, and that copy is what
      // readiness/preview/tokens/send read from then on (nothing downstream
      // changes — `mapping.sender` is still the single read, Decision 11).
      //
      // The sequence's sender WINS when it has one: sender is locked to the
      // sequence, so re-pointing a rung at a sequence with a sender must adopt
      // that sequence's sender rather than keep the previous sequence's. The
      // `currentMapping?.sender` fallback only applies when the target
      // sequence has NO sender (null/''), which keeps a rung that was already
      // ready from silently becoming unready on a swap (Decision 10 ships no
      // backfill, so most existing sequences are null here).
      sender: selected.sender ?? currentMapping?.sender ?? '',
    };

    // E2a — see this file's module doc: a swap AWAY from an already-mapped
    // sequence loses that association, so it goes through the confirm diff
    // instead of staging immediately.
    //
    // [R11] Re-saving the row this group already uses (the Library row or
    // its copy — each is its own row now) is "keep what it uses", not a swap.
    // Nothing changed, so nothing is written — the drawer just closes.
    if (currentMapping !== null && currentMapping.sequenceId === selected.id) {
      onClose();
      return;
    }
    if (currentMapping !== null) {
      void confirmSwap(currentMapping.sequenceId, selected, mapping);
      return;
    }

    void setPlaybookDraftRungMapping(apiFetch, playbookId, draftKey, mapping)
      .then((response) => {
        if (!response) return undefined;
        // Root cause (b), belt-and-suspenders (round-3-QA-fixes plan, Unit
        // C3) — mapping THIS sequence onto a rung changes its
        // `activeMappingCount`/usage everywhere else it's already mapped;
        // nothing else in this write-through path invalidates the sequence
        // detail cache, so a rung already showing this sequence's editor
        // would otherwise keep reading a stale count.
        void queryClient.invalidateQueries({ queryKey: sequenceDetailQueryKey(selected.id) });
        return applyLadderSaveToCache(queryClient, playbookId, response, playId);
      })
      .catch((error) => {
        addErrorToast({
          content: error instanceof Error ? error.message : 'Could not update this mapping — try again.',
        });
      });
    onClose();
  }

  function handleSave() {
    if (sequenceId === null) {
      setShowErrors(true);
      return;
    }
    const selected = sequences?.find((sequence) => sequence.id === sequenceId);
    if (!selected) {
      // Can only happen if the library refetched the selection away mid-drawer.
      setShowErrors(true);
      return;
    }
    commitSelection(selected);
  }

  /**
   * Round-3-QA-fixes plan (U4, decision 4) — `SequenceCreateFields` (lifted
   * from `PlaybooksConfigPage.tsx`) never calls `onSave` without a channel
   * (its own Save button is gated on one being picked), and for
   * `gong_engage` specifically it is additionally gated on a picked flow —
   * the `if (!flow)` check below is defense in depth, not a reachable
   * rep-facing state, same reasoning as Config's own `handleCreateSave`.
   *
   * Gong branches to `createSequenceFromGongFlow` instead of the plain
   * `createSequence`, with the SAME `GongSequencePartialImportError`
   * handling Config uses: that error means a real (if unbound/empty)
   * sequence now sits in the library, so the library is invalidated and it
   * is still selected here — never "nothing happened" — while a PRE-create
   * failure (any other error, nothing was created) rethrows into the outer
   * `catch` below for its ordinary generic-toast/leave-open handling.
   *
   * R5-11 — the Gong success path no longer bounces back to the picker
   * (screen 1) with a checked radio, requiring a second manual "Save
   * sequence" click. `createSequenceFromGongFlow`'s response carries only
   * `sequenceId` (not the full `PlaybooksSequenceSummary` `commitSelection`
   * needs — channel/sender/etc.), and the brand-new id isn't in the still-
   * stale `sequences` prop yet, so its detail is fetched once via
   * `queryClient.fetchQuery` (same query key `SequencesStep`/`confirmSwap`
   * already share) and committed directly — 3→4 (a confirm-replace, if this
   * rung already had a different mapping) or 3→close. The
   * `GongSequencePartialImportError` branch is UNCHANGED (module doc/plan
   * landmine: it's an error path, not part of this fix) — it still bounces
   * to the picker with the partially-created row selected, since there is
   * no steps-loaded sequence detail worth committing to yet. The plain
   * (non-Gong) branch is also unchanged for the same reason — the task this
   * fix addresses is the Gong-flow-browse doorway specifically.
   */
  async function handleCreateSequence(
    name: string,
    channel: LauncherChannel,
    /** Playbooks QA round 4, U9 (SN1) — the new sequence's locked sender; `null` = "Not set". */
    sender: string | null,
    flow?: GongFlowSummary,
  ) {
    setIsSavingNewSequence(true);
    try {
      if (channel === 'gong_engage') {
        if (!flow) throw new Error('Pick a Gong flow.');
        let createdId: string;
        try {
          createdId = (
            await createSequenceFromGongFlow(apiFetch, {
              flow,
              name,
              sender,
              ...(playId === undefined ? {} : { owningPlayId: playId }),
            })
          ).sequenceId;
        } catch (err) {
          if (!(err instanceof GongSequencePartialImportError)) {
            // Pre-create failure — nothing was created. Fall through to the
            // outer catch's generic toast, and leave the form open.
            throw err;
          }
          void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
          setSequenceId(err.sequenceId);
          setIsCreatingSequence(false);
          addErrorToast({
            content: `Created "${name}" but couldn't load its steps from Gong. Open it in a play and use "Load steps from Gong".`,
          });
          return;
        }
        // Codex review of Playbooks QA round 5 (finding B2) — the sequence
        // row is now CREATED and must be treated as terminal success from
        // here, exactly like the plain (non-Gong) branch below closes/toasts
        // /selects the instant its own `createSequence` resolves, before
        // doing anything else that could fail. The detail fetch + auto-
        // commit right below is a best-effort convenience layered ON TOP of
        // that already-successful creation, never allowed to make the
        // creation itself look retryable — before this fix, a transient
        // failure in the fetch below fell into the outer `catch`, which left
        // the create form open and enabled while the sequence had ALREADY
        // been created; a rep's retry click called `createSequenceFromGongFlow`
        // again with no dedupe, spawning a real duplicate sequence.
        void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
        addToast({ content: `Created "${name}".`, status: 'success' });
        setSequenceId(createdId);
        setIsCreatingSequence(false);
        setGongCreatePrefillFlow(undefined);
        try {
          const created = await queryClient.fetchQuery({
            queryKey: sequenceDetailQueryKey(createdId),
            queryFn: () => getSequence(apiFetch, createdId),
          });
          commitSelection(created.sequence);
        } catch {
          // The create form is already closed and cannot be resubmitted, so
          // there is no duplicate-creation risk here — this falls back to
          // the ORIGINAL (pre-R5-11) recovery shape instead: `setSequenceId`
          // above already has the new sequence pre-selected on screen 1, so
          // "Save sequence" there finishes the mapping without creating
          // anything twice.
          addErrorToast({
            content: `Created "${name}", but couldn't load it into this group yet — pick "Save sequence" below to finish mapping it.`,
          });
        }
        return;
      }
      // Plan 002, U6 (Decision 13) — inside a play the new sequence is the
      // play's own; the playbook doorway's call (and wire body) is unchanged.
      const createdId = (
        playId === undefined
          ? await createSequence(apiFetch, name, channel, sender)
          : await createSequence(apiFetch, name, channel, sender, { owningPlayId: playId })
      ).id;
      void queryClient.invalidateQueries({ queryKey: libraryQueryKey() });
      setSequenceId(createdId);
      setIsCreatingSequence(false);
      addToast({ content: `Created "${name}".`, status: 'success' });
    } catch (error) {
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not create this Sequence — try again.',
      });
    } finally {
      setIsSavingNewSequence(false);
    }
  }

  /**
   * D2/D4 (Batch 6) — the Gong tab's ONE handler for every flow-row click.
   * `resolveGongFlowPick` (`gong-flow-picker-api.ts`) owns the three-way
   * decision and does every write that doesn't need a confirm; this function
   * only reacts to whichever outcome came back, matching each case's own
   * cache-patch/toast/selection shape.
   */
  function findMappedSequence(): PlaybooksSequenceSummary | undefined {
    return currentMapping ? sequences?.find((sequence) => sequence.id === currentMapping.sequenceId) : undefined;
  }

  /**
   * Same cache-write shape as `SequencesStep.tsx`'s `applySequenceHeadToCache`
   * — not imported from there (that function is component-scoped, not
   * exported), so this is a same-shape local duplicate rather than a shared
   * export forced into existence for one more call site.
   */
  function patchSequenceHeadCache(
    sequenceId: string,
    response: { versionId: string; versionNumber: number | null; steps: PlaybooksSaveSequenceStepsResponse['steps'] },
  ) {
    queryClient.setQueryData(
      sequenceDetailQueryKey(sequenceId),
      (old: { sequence: PlaybooksSequenceSummary; head: unknown } | undefined) =>
        old
          ? {
              ...old,
              head: {
                versionId: response.versionId,
                versionNumber: response.versionNumber,
                steps: response.steps,
              },
            }
          : old,
    );
  }

  /**
   * Fix 5 (Live-QA round 2, Batch 6) — synchronously patches the library
   * list's matching sequence's `flowId`, in addition to the existing
   * `invalidateQueries` background refetch. Before this fix, the REBIND
   * success handler only fired an unawaited
   * `queryClient.invalidateQueries({ queryKey: libraryQueryKey() })`
   * for the library's copy of `flowId` — no synchronous patch. If that
   * background refetch is slow or fails silently, `findMappedSequence()`
   * keeps reading the STALE `sequences` prop, so a rep who immediately
   * reopens this drawer and re-picks the SAME flow they just rebound to
   * misses `resolveGongFlowPick`'s `mappedSequence.flowId === flow.flow_id`
   * "unchanged" shortcut — it re-fetches detail and shows a
   * confusingly-labeled confirm dialog claiming to rebind "from" the flow
   * it's already on. This keeps the invalidate as a background consistency
   * check but no longer depends on it for correctness in the interim.
   *
   * Case 1's "selected" outcome (`handlePickGongFlow`, below) has this
   * identical gap — pre-existing, NOT part of this batch's scope (see the
   * bug list) — so only this rebind success site is patched.
   */
  function patchLibraryFlowIdCache(sequenceId: string, flowId: string) {
    // Plan 2026-09-27-007 U3 — the entry this drawer's `sequences` came from:
    // play-scoped inside a play, the unscoped Library outside one.
    queryClient.setQueryData<PlaybooksLibraryResponse>(libraryQueryKey(playId), (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sequences: prev.sequences.map((sequence) =>
          sequence.id === sequenceId ? { ...sequence, flowId } : sequence,
        ),
      };
    });
  }

  /**
   * Best-effort flow display name, resolved from `LauncherGongFlowBrowse`'s
   * own warm browse-catalog cache (round-3b QA fixes, G3 — Flow's real
   * `FLOW_GONG_BROWSE_KEY`, not the deleted `GongFlowPickerInline`'s private
   * key). A catalog miss falls back to `gongFlowDisplayName`'s generic
   * phrase (plan 2026-09-28-008 U4, R20/F9g) — never the raw flow id, which
   * both this and `SequencesStep.tsx`'s `lockedFlowName` used to show.
   * `null` (rather than that fallback phrase) means "there is no old flow to
   * name" — the rare D2-case-2 edge case where `fromFlowId` itself is
   * `null`.
   */
  function resolvedFlowName(flowId: string | null): string | null {
    if (flowId === null) return null;
    const browse = queryClient.getQueryData<GongFlowBrowseResponse>(FLOW_GONG_BROWSE_KEY);
    // Plan 2026-09-28-008 U4 (R20/F9g) — a catalog miss falls back to a
    // generic phrase, never the raw flow id.
    return gongFlowDisplayName(browse?.flows.find((f) => f.flow_id === flowId)?.name);
  }

  /**
   * D4 — case 3's confirm-or-cancel. Everything the dialog shows
   * (`resolveGongFlowPick` already fetched and mapped both step sets) is
   * resolved BEFORE this opens, matching `confirmSwap`'s own fetch-before-open
   * rule (this file's module doc) — the dialog's `content` is a JSX snapshot,
   * not something that re-renders off later state.
   *
   * Plan 2026-09-28-006 U6 (rulebook R5, R6, R8, R9, R17, R18) — inside a
   * play the rebind is gated by USE, never by ownership: this group's usage
   * places are fetched fresh (staleTime 0) BEFORE the dialog opens (a failed
   * fetch toasts and opens nothing), then —
   *  - used by this group alone (`inPlayUsageGate(places, origin) === 'silent'`): one
   *    "Load steps" — an owned copy is written directly with `playId`; a
   *    Library sequence is copied for THIS group (`ensurePlayOwned({ rungId,
   *    mode: 'ensure' })`) on the click, then loaded onto the copy;
   *  - shared: the ONE Save menu (`SaveMenu label="Load steps"` over
   *    `saveMenuOptions`), the same three choices every other in-play edit
   *    gets — ① a copy for this group only (`mode: 'fork'`), ② "Update N
   *    plays" (`POST /update-plays` with a `'rebind'` change; an owned copy
   *    shared by this play's groups is written in place), ③ "Update plays
   *    and the playbook" (the Library write, reachable only through the
   *    menu's own confirm). Cancel writes nothing.
   * The playbook doorway (`playId === undefined`) is unchanged: one danger
   * "Load steps" that rewrites the Library sequence.
   */
  async function openRebindConfirm(
    outcome: Extract<GongFlowPickOutcome, { kind: 'confirm-rebind' }>,
    flow: GongFlowSummary,
  ) {
    const fromName = resolvedFlowName(outcome.fromFlowId) ?? 'the current copy';
    // Codex fix 3 (round 2 of Batch 6 hardening) — the success handler below
    // clears drafts ACROSS EVERY rung/scope mapping this sequence (via
    // `clearCopyDraftsForSequence`, an identity-level clear, since a rebind
    // rewrites this Sequence's SHARED head). The warning check has to match
    // that true clearing scope — checking only this rung's own `draftKey`
    // scope (`sequenceHasCopyDrafts`) would miss a DIFFERENT rung's unsaved
    // drafts against this same shared sequence and silently under-warn.
    const hasUnsavedDrafts = sequenceHasAnyCopyDrafts(useSequenceCopyDraftStore.getState(), outcome.sequenceId);
    const lossClause = hasUnsavedDrafts ? ', plus any unsaved copy drafts anyone has typed against it,' : '';
    // An empty head reaching this dialog is a first bind or a rebind with no
    // SAVED steps to lose — lighter wording than the destructive-swap
    // phrasing, but the draft-loss warning still applies.
    const baseText =
      outcome.headSteps.length === 0
        ? outcome.fromFlowId === null
          ? `This sequence has no steps yet. Loading "${flow.name}" will link it to that flow${lossClause}.`
          : `This sequence has no steps yet, but it's currently linked to "${fromName}". ` +
            `Loading "${flow.name}" will switch it${lossClause}.`
        : `This sequence is currently linked to "${fromName}". Loading "${flow.name}" replaces its steps` +
          `${lossClause}. This can't be undone.`;

    /** The success path every write shape shares. */
    function onLoaded(sequenceId: string, save: PlaybooksSaveSequenceStepsResponse, libraryKeyPlayId?: string) {
      // Fix 3 — a rebind rewrites this Sequence's head, so every rung/playbook
      // mapping it (not just this one) must have its stale drafts cleared,
      // not only this rung's own `draftKey`-scoped ones.
      clearCopyDraftsForSequence(sequenceId);
      // Codex F1, layer (a) — the SAME argument for the other store. A
      // generated review is keyed by position, and a rebind can put a Call /
      // LinkedIn step at a position that held an email step (and carried an
      // ACCEPTED review); clearing hand-edit drafts alone left that review
      // alive to be applied onto the new step. Both stores, one rebind.
      clearGeneratedCopyForSequence(sequenceId);
      patchSequenceHeadCache(sequenceId, save);
      // Fix 5 — synchronous patch alongside the background
      // consistency-check invalidate.
      patchLibraryFlowIdCache(sequenceId, flow.flow_id);
      void queryClient.invalidateQueries({ queryKey: libraryQueryKey(libraryKeyPlayId) });
      addToast({ content: `Loaded ${save.steps.length} steps from "${flow.name}".`, status: 'success' });
    }

    function showRebindError(error: unknown) {
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not load this Gong flow — try again.',
      });
    }

    /** The doorway's "Load steps" and the in-play ③ (after its confirm): a Library write, no `playId`. */
    function loadOntoLibrary() {
      void commitGongFlowRebind(apiFetch, {
        sequenceId: outcome.sequenceId,
        flowId: flow.flow_id,
        fromFlowId: outcome.fromFlowId,
        headVersionId: outcome.headVersionId,
        mappedSteps: outcome.mappedSteps,
        // Round-3-QA-fixes plan, Unit C3 / decision 10 — the exact count this
        // confirm's own copy was built from (root cause (a): omitting this
        // made the server default to `1` and 409 a rebind on any sequence
        // used in 2+ places).
        activeMappingCount: outcome.activeMappingCount,
        // Round-4 QA codex-review fix (wave 1, finding 1) — the second axis of
        // the same acknowledgment, resolved in the same `getSequence` this
        // dialog's copy was built from.
        activeUnsentPlayCount: outcome.activeUnsentPlayCount,
      })
        // The unscoped key prefix-matches the play-scoped entries too: a
        // Library write changes what every scope reads.
        .then((save) => onLoaded(outcome.sequenceId, save))
        .catch(showRebindError);
      closeAlertDialog();
    }

    function openDialog(descriptionText: string, actions: ReactNode) {
      openAlertDialog({
        size: 'lg',
        // R5-11 — same 680px widening as `confirmSwap`'s dialog above, same
        // reason (Tailwind v4 important-variant needed to beat the `lg` size
        // token under Glaze's plain-`clsx` composition).
        className: 'sm:max-w-[680px]!',
        content: (
          <>
            <AlertDialogHeader onClose={closeAlertDialog}>
              <AlertDialogTitle>
                Replace &quot;{fromName}&quot; with &quot;{flow.name}&quot;?
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody className="flex flex-col gap-4">
              <AlertDialogDescription>{descriptionText}</AlertDialogDescription>
              <CopyDiffView
                headSteps={outcome.headSteps}
                generatedSteps={outcome.mappedSteps}
                // Both sides are the same Gong sequence by construction — `outcome`
                // (from `resolveGongFlowPick`) carries no channel field.
                headChannel="gong_engage"
                generatedChannel="gong_engage"
                isHeadStale={false}
                leftColumnLabel={fromName}
                rightColumnLabel={flow.name}
              />
            </AlertDialogBody>
            <AlertDialogActions>
              <Button variant="secondary" onClick={closeAlertDialog}>
                Cancel
              </Button>
              {actions}
            </AlertDialogActions>
          </>
        ),
      });
    }

    if (playId === undefined) {
      const sharedClause =
        outcome.activeMappingCount > 1
          ? ` This sequence is used in ${outcome.activeMappingCount} places. They will all switch to the new flow too.`
          : '';
      openDialog(
        `${baseText}${sharedClause}`,
        <Button variant="danger" onClick={loadOntoLibrary}>
          Load steps
        </Button>,
      );
      return;
    }
    const inPlayId = playId;

    let usage: PlaybooksUsageResponse;
    try {
      usage = await queryClient.fetchQuery({
        queryKey: sequenceUsageQueryKey(outcome.sequenceId),
        queryFn: () => getSequenceUsage(apiFetch, outcome.sequenceId),
        staleTime: 0,
      });
    } catch (error) {
      showRebindError(error);
      return;
    }
    const places = usage.places;
    const mappedSequence = sequences?.find((sequence) => sequence.id === outcome.sequenceId);
    const owned = mappedSequence !== undefined && isOwnedByPlay(mappedSequence, inPlayId);

    /**
     * Plan 2026-09-28-008 U2 (rulebook R14) — a stale refusal: the gate's
     * `'stale'` ({@link GROUP_MOVED_SENTENCE}) or a 409 `version_conflict`
     * from a rebind write (the server's sentence). Nothing reloads on its
     * own: this dialog (its own host — it never parks a pending in the
     * usage-sidebar store) shows the sentence with one "Reload to see their
     * change" button, and only that button re-reads the play's groups
     * ({@link adoptFreshPlayLadder}, drafts carried) so the rep can pick the
     * flow again against the group as it now stands. The pick itself is the
     * only thing typed here, so closing loses nothing.
     */
    function showStaleRefusal(sentence: string) {
      openDialog(
        sentence,
        <Button variant="primary" onClick={reloadAfterStaleRefusal}>
          Reload to see their change
        </Button>,
      );
    }

    function reloadAfterStaleRefusal() {
      closeAlertDialog();
      void adoptFreshPlayLadder(apiFetch, queryClient, {
        playbookId,
        playId: inPlayId,
        kind: 'sequence',
        identityIds: [outcome.sequenceId],
      })
        .then(() => queryClient.invalidateQueries({ queryKey: sequenceUsageQueryKey(outcome.sequenceId) }))
        .catch(() => addErrorToast({ content: USAGE_SIDEBAR_RELOAD_FAILED_MESSAGE }));
    }

    /** A rebind write's failure: a stale refusal parks in {@link showStaleRefusal}; anything else toasts. */
    function showInPlayRebindError(error: unknown) {
      if (isVersionConflictError(error)) {
        showStaleRefusal(error instanceof Error ? error.message : GROUP_MOVED_SENTENCE);
        return;
      }
      showRebindError(error);
    }

    /**
     * A write to this play's own copy. `how`:
     *  - `'direct'`: `outcome.sequenceId` IS this play's copy — written in
     *    place, acknowledging `counts` (the places this dialog was built from);
     *  - `'ensure'` / `'fork'`: copy the Library sequence (or, for `'fork'`,
     *    this play's copy) for THIS group only on the click — never before a
     *    choice — quoting the head this dialog's diff was built from, then load
     *    onto the copy (a fresh copy is used in exactly one place: counts 1/1).
     * `ensurePlayOwned` adopts the rewritten ladder and rekeys this group's
     * drafts; the write then always runs — a fork is never left without it.
     */
    function loadOntoPlayCopy(
      how: { kind: 'direct'; activeMappingCount: number; activeUnsentPlayCount: number } | { kind: 'ensure' | 'fork' },
    ) {
      closeAlertDialog();
      // Plan 2026-09-29-001 U1 (R3/R5/R14) — every in-play rebind names its
      // group, `'direct'` (this sequence written in place) included; the
      // `how.kind !== 'direct' &&` qualifier that used to exempt it is
      // deleted.
      if (rungId === null) {
        addErrorToast({ content: SAVE_GROUP_FIRST });
        return;
      }
      const originRungId = rungId;
      // [R14] another editor may already have copied this sequence into the
      // play: a `version_conflict` from the fork or the load is refused with
      // the server's sentence and a Reload button — never reloaded on its
      // own (plan 2026-09-28-008 U2).
      void (async () => {
        const target =
          how.kind === 'direct'
            ? { ownedId: outcome.sequenceId, headVersionId: outcome.headVersionId }
            : await ensurePlayOwned(apiFetch, queryClient, {
                playId: inPlayId,
                playbookId,
                kind: 'sequence',
                identityId: outcome.sequenceId,
                rungId: originRungId,
                mode: how.kind,
                expectedSourceVersionId: outcome.headVersionId,
              });
        const isFreshCopy = target.ownedId !== outcome.sequenceId;
        // Plan 2026-09-29-001 U1 (R5/R8) — `'direct'` names the fan-out the
        // rep was shown (falling back to just this group when the origin
        // play's own entry is empty/absent); a fresh copy is used in
        // exactly one place, this group.
        const confirmedRungIds =
          how.kind === 'direct'
            ? confirmedFanOutSet(places, originRungId).plays.find((p) => p.playId === inPlayId)?.rungIds ?? [originRungId]
            : [originRungId];
        const save = await commitGongFlowRebind(apiFetch, {
          sequenceId: target.ownedId,
          flowId: flow.flow_id,
          fromFlowId: outcome.fromFlowId,
          headVersionId: target.headVersionId,
          mappedSteps: outcome.mappedSteps,
          activeMappingCount: how.kind === 'direct' ? how.activeMappingCount : isFreshCopy ? 1 : outcome.activeMappingCount,
          activeUnsentPlayCount:
            how.kind === 'direct' ? how.activeUnsentPlayCount : isFreshCopy ? 1 : outcome.activeUnsentPlayCount,
          playId: inPlayId,
          group: { rungId: originRungId, confirmedRungIds },
        });
        onLoaded(target.ownedId, save, inPlayId);
        // [R11] The copy is its own picker row — select it.
        setSequenceId(target.ownedId);
      })().catch(showInPlayRebindError);
    }

    /**
     * [R8] ② on a Library sequence: every listed draft play gets its own copy
     * for each group still on the Library sequence, carrying the new flow and
     * its steps — one server operation; the Library is untouched. The origin
     * play's rewritten ladder is adopted (this group last), then this group's
     * new copy is selected.
     */
    function loadViaUpdatePlays() {
      closeAlertDialog();
      if (rungId === null) {
        addErrorToast({ content: SAVE_GROUP_FIRST });
        return;
      }
      const originRungId = rungId;
      void (async () => {
        const result = await updatePlays(apiFetch, inPlayId, {
          kind: 'sequence',
          identityId: outcome.sequenceId,
          expectedSourceVersionId: outcome.headVersionId,
          // The request names the set the rep was shown (R8) — the same set the label counts.
          confirmed: confirmedFanOutSet(places, originRungId),
          change: { field: 'rebind', flowId: flow.flow_id, steps: outcome.mappedSteps },
        });
        // [R8] Plan 2026-09-28-007 U8 (Codex c2-1) — the copies carry the NEW
        // flow's steps, so this group's unsaved typing and staged AI review on
        // the old flow are dropped, never rekeyed onto the copies.
        await adoptUpdatePlaysResult(queryClient, {
          playbookId,
          playId: inPlayId,
          kind: 'sequence',
          fromId: outcome.sequenceId,
          result,
          drafts: 'clear',
        });
        const originEntry = result.updatedPlays.find((entry) => entry.playId === inPlayId);
        const moved = originEntry?.rungs.find((entry) => entry.rungId === originRungId);
        if (moved !== undefined) {
          patchSequenceHeadCache(moved.copyId, {
            versionId: moved.headVersionId,
            versionNumber: null,
            // What the copies were written with (normalized), not what was posted.
            steps: result.steps ?? outcome.mappedSteps,
          });
          setSequenceId(moved.copyId);
        }
        void queryClient.invalidateQueries({ queryKey: libraryQueryKey(inPlayId) });
        void queryClient.invalidateQueries({ queryKey: sequenceUsageQueryKey(outcome.sequenceId) });
        const plays = result.updatedPlays.length;
        addToast({
          content:
            plays > 1
              ? `Updated ${pluralize(plays, 'play')}.`
              : `Updated ${pluralize(originEntry?.rungs.length ?? 1, 'group')} in this play.`,
          status: 'success',
        });
      })().catch(showInPlayRebindError);
    }

    // {@link gateInPlayWrite} (`ensure-play-owned.ts`) is the ONE client
    // gate every in-play/in-playbook save goes through — throws
    // `SAVE_GROUP_FIRST` for a rung staged this session (no server id yet,
    // can't be matched against `places`; the outer `catch` toasts it), and
    // returns `'stale'` when the group moved since the rep loaded it: refused
    // with the plain "group moved" sentence and a Reload button, no request.
    const origin: UsageSidebarOrigin = { kind: 'play', playId: inPlayId, campaignSfdcId: '', playbookId, rungId, rungDraftKey: draftKey };
    const gate = gateInPlayWrite({ places, origin });
    if (gate === 'stale') {
      showStaleRefusal(GROUP_MOVED_SENTENCE);
      return;
    }
    if (gate === 'silent') {
      openDialog(
        baseText,
        <Button
          variant="primary"
          onClick={() =>
            loadOntoPlayCopy(
              owned
                ? {
                    kind: 'direct',
                    activeMappingCount: outcome.activeMappingCount,
                    activeUnsentPlayCount: outcome.activeUnsentPlayCount,
                  }
                : { kind: 'ensure' },
            )
          }
        >
          Load steps
        </Button>,
      );
      return;
    }

    // The ONE Save menu, over a pending built for it alone (never written to
    // the usage-sidebar store — this dialog is its own host). Only the fields
    // `saveMenuOptions` reads carry meaning; `campaignSfdcId` is not read by
    // it (the origin card is matched on playbook + group + play).
    const syntheticPending: PendingUsageSidebar = {
      mode: 'save',
      target: {
        kind: 'sequence',
        id: outcome.sequenceId,
        name: mappedSequence?.name ?? '',
        owningPlayId: mappedSequence?.owningPlayId ?? null,
        archivedInLibrary: mappedSequence !== undefined && isArchivedInLibrary(mappedSequence),
      },
      origin,
      draftSteps: outcome.mappedSteps,
      headVersionId: outcome.headVersionId,
      places,
      ownVersionPlays: usage.ownVersionPlays ?? [],
      templatePlaybooks: owned ? [] : (usage.templatePlaybooks ?? []),
      acknowledgedCount: places.length,
      acknowledgedUnsentPlayCount: distinctUnsentPlays(places),
      decision: null,
      requestId: '',
      status: 'idle',
      originMissing: false,
      staleRefusal: null,
    };
    const headline = usageHeadline(usageCards(places), 'sequence');
    openDialog(
      `${baseText} ${headline}`,
      <SaveMenu
        label="Load steps"
        options={saveMenuOptions(syntheticPending)}
        disabled={false}
        onChoose={(decision) => {
          if (decision === 'save-new') loadOntoPlayCopy({ kind: 'fork' });
          else if (decision === 'update-plays') {
            if (owned) {
              loadOntoPlayCopy({
                kind: 'direct',
                activeMappingCount: places.length,
                activeUnsentPlayCount: distinctUnsentPlays(places),
              });
            } else loadViaUpdatePlays();
          } else loadOntoLibrary();
        }}
      />,
    );
  }

  async function handlePickGongFlow(flow: GongFlowSummary) {
    if (isPickingFlow) return;
    setIsPickingFlow(true);
    try {
      // Plan 2026-09-27-007 U3 — never fork before a confirm; ownership is
      // resolved when the rep's choice is known. The only outcome that writes
      // without a dialog (a first bind onto an unbound, empty sequence) calls
      // `ensureOwned` right before its write; a confirm-rebind resolves
      // ownership from the button the rep clicks (`openRebindConfirm`), so a
      // Cancel leaves no copy and no "Edited in this play" tag behind.
      const outcome = await resolveGongFlowPick(apiFetch, {
        flow,
        mappedSequence: findMappedSequence(),
        // [R18] Reuse only what the picker itself would offer (never an archived Library row).
        librarySequences: pickerSequences ?? [],
        ...(playId === undefined
          ? {}
          : {
              owningPlayId: playId,
              // Plan 2026-09-29-001 U1 (R3/R5/R14) — the group the one
              // no-dialog write below names; `undefined` here only when
              // `ensureOwned` itself would reject with `SAVE_GROUP_FIRST`.
              rungId: rungId ?? undefined,
              // [R3] The one no-dialog write (a first bind this group alone
              // uses) copies for THIS group only. [R14] Plan 2026-09-28-007
              // U8 (Codex c2-3) — the copy quotes the head the pick decided
              // on, so steps someone else just added are never overwritten.
              ensureOwned: (identityId: string, expectedSourceVersionId: string) => {
                if (rungId === null) return Promise.reject(new Error(SAVE_GROUP_FIRST));
                return ensurePlayOwned(apiFetch, queryClient, {
                  playId,
                  playbookId,
                  kind: 'sequence',
                  identityId,
                  rungId,
                  mode: 'ensure',
                  expectedSourceVersionId,
                });
              },
            }),
      });
      switch (outcome.kind) {
        case 'selected': {
          // Plan 2026-09-27-007 U3 — the key this drawer reads (play-scoped in a play).
          void queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
          setSequenceId(outcome.sequenceId);
          addToast({
            content: outcome.created ? `Created "${flow.name}" from the Gong flow.` : `Selected "${flow.name}".`,
            status: 'success',
          });
          break;
        }
        case 'unchanged': {
          setSequenceId(outcome.sequenceId);
          break;
        }
        case 'imported': {
          patchSequenceHeadCache(outcome.sequenceId, outcome.save);
          // [R11] Plan 2026-09-28-007 U8 (Codex c2-6) — a first load inside a
          // play lands on this group's copy; select it so the next Save
          // proposes no swap back to the Library row.
          setSequenceId(outcome.sequenceId);
          void queryClient.invalidateQueries({ queryKey: libraryQueryKey(playId) });
          addToast({ content: `Loaded ${outcome.stepCount} steps from "${outcome.flowName}".`, status: 'success' });
          break;
        }
        case 'confirm-rebind': {
          await openRebindConfirm(outcome, flow);
          break;
        }
      }
    } catch (error) {
      addErrorToast({
        content: error instanceof Error ? error.message : 'Could not load this Gong flow — try again.',
      });
    } finally {
      setIsPickingFlow(false);
    }
  }

  /**
   * R5-11 — the Gong tab's "Browse Gong flows" doorway (below), the pure
   * "browse a Gong flow directly" path Spencer's QA reported: no bounce
   * through the picker (screen 1) with a silently-created null-sender
   * sequence.
   *
   * An ALREADY Gong-mapped rung (this rung's `currentMapping` already points
   * at a `gong_engage` sequence) is a DIFFERENT case — unchanged/imported/
   * confirm-rebind, all handled correctly today — and still routes through
   * `handlePickGongFlow`/`resolveGongFlowPick` exactly as before; this
   * function only changes the branch `resolveGongFlowPick`'s own case 1
   * covered (rung unmapped, or mapped to a non-Gong sequence).
   */
  function handleBrowseGongFlowPick(flow: GongFlowSummary) {
    setGongBrowseOpen(false);
    const mappedSequence = findMappedSequence();
    if (mappedSequence && mappedSequence.channel === 'gong_engage') {
      void handlePickGongFlow(flow);
      return;
    }
    // Same reuse check `resolveGongFlowPick`'s case 1 makes internally —
    // picking the same flow from two different rungs must not spawn two
    // Sequences for it. A hit commits straight through (screen 2 -> 4, or
    // 2 -> close); a miss opens the create form (screen 3) pre-filled with
    // this flow instead of silently creating one with no sender.
    // [R18] Plan 2026-09-28-007 U8 (Codex b2-3) — the same eligible list the
    // radio picker renders, so an archived Library sequence is never offered.
    const reused = findLibrarySequenceForFlow(pickerSequences ?? [], flow.flow_id);
    if (reused) {
      commitSelection(reused);
      return;
    }
    setGongCreatePrefillFlow(flow);
    setIsCreatingSequence(true);
  }

  const options = (filteredSequences ?? []).map((sequence, index) => {
    const detail = detailQueries[index]?.data;
    const firstStep = detail?.head?.steps[0];
    const previewText = firstStep
      ? firstStep.subject || (firstStep.body ?? '').split('\n')[0]
      : undefined;
    // [R11][R19] `null` outside a play (`playId === undefined`), since
    // `pickerTag` only tags a row against THIS play's ownership. An owned row
    // (a copy, or a play-created original) says which of this play's groups
    // use it instead of a first-step preview, and carries no "N playbooks"
    // badge — no playbook can reference a play's own row.
    const tag = playId === undefined ? null : pickerTag(sequence, playId);
    const owned = tag !== null;
    return {
      value: sequence.id,
      label: sequence.name,
      description: owned ? ownedRowUseText(sequence.id, ladderRungs, segmentationNameById, 'sequence') : previewText,
      trailing: (
        <div className="flex items-center gap-1">
          <Badge color={channelBadgeColor(sequence.channel)}>{channelLabel(sequence.channel)}</Badge>
          {sequence.headVersionNumber != null && <Badge>v{sequence.headVersionNumber}</Badge>}
          <Badge>
            {sequence.stepCount} step{sequence.stepCount === 1 ? '' : 's'}
          </Badge>
          {detail && !owned && (
            <Badge>
              {detail.activeMappingCount} playbook{detail.activeMappingCount === 1 ? '' : 's'}
            </Badge>
          )}
          {tag !== null && <Badge color="blue">{tag}</Badge>}
        </div>
      ),
    };
  });

  return (
    <PushDrawer
      title={`Sequence for ${rungName}`}
      subtitle="People who land in this segment get whichever sequence you pick here."
      onClose={onClose}
      footer={
        isCreatingSequence ? undefined : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isCheckingSwap}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isCheckingSwap}>
              Save sequence
            </Button>
          </>
        )
      }
    >
      {/*
        [R18] The group's CURRENT sequence was archived in the Library: it
        keeps sending, but it is never a picker row again (`pickerRows`), so
        its name is shown here with the tag instead.
      */}
      {currentArchivedRow !== undefined && (
        <div className="flex items-center gap-2">
          <Text size="xs" variant="tertiary">
            Currently uses &quot;{currentArchivedRow.name}&quot;
          </Text>
          <StatusBadge status="neutral">Archived in Library</StatusBadge>
        </div>
      )}
      {isCreatingSequence ? (
        <SequenceCreateFields
          saving={isSavingNewSequence}
          onSave={(name, channel, sender, flow) =>
            void handleCreateSequence(name, channel, sender, flow)
          }
          onCancel={() => {
            setIsCreatingSequence(false);
            setGongCreatePrefillFlow(undefined);
          }}
          // R5-11 — pre-filled when this form was opened from the Gong tab's
          // "Browse Gong flows" doorway for a flow no library sequence
          // already covers (`handleBrowseGongFlowPick`); `undefined` (this
          // form's default) for the plain "Create a new Sequence" doorway
          // below.
          initialChannel={gongCreatePrefillFlow ? 'gong_engage' : undefined}
          initialFlow={gongCreatePrefillFlow}
        />
      ) : (
        <>
          <SegmentedControl size="sm" value={channelFilter} onValueChange={setChannelFilter}>
            {CHANNEL_FILTER_OPTIONS.map((option) => (
              <SegmentedControlOption key={option.value} value={option.value}>
                {option.label}
              </SegmentedControlOption>
            ))}
          </SegmentedControl>
          <DebouncedSearchInput
            aria-label="Search sequences"
            placeholder="Search sequences"
            initialValue={search}
            onSearch={setSearch}
          />
          {channelFilter === 'gong_engage' ? (
            <>
              {/*
                D2 — existing Gong sequences still list normally above the
                picker (no empty-state text of their own here: an empty list
                just means the "Start from a Gong flow" picker below is the
                only affordance, which is the normal first-time state, not an
                error).
              */}
              {sequences === undefined ? (
                <Text size="xs" variant="tertiary">
                  Loading sequences…
                </Text>
              ) : filteredSequences && filteredSequences.length > 0 ? (
                <RadioGroupField
                  label="Sequence"
                  value={sequenceId}
                  onValueChange={(value) => setSequenceId(value)}
                  options={options}
                  errorMessage={showErrors && sequenceError !== null ? sequenceError : undefined}
                />
              ) : null}
              {/*
                Fix 4 (Live-QA round 2, Batch 6) — the "Browse Gong flows"
                button below (and the panel it opens) is hidden until
                `sequences` resolves. `findMappedSequence()` needs a resolved
                `sequences` prop to tell an already-mapped rung apart from an
                unmapped one; picking a flow before it resolves makes
                `resolveGongFlowPick` receive `mappedSequence: undefined` and
                wrongly take case 1 (create-or-reuse) even for an
                already-mapped rung, potentially spawning a duplicate sequence
                and orphaning the real one. Same loading guard as the Radio
                list immediately above.
              */}
              {sequences === undefined ? null : (
                <div className="flex flex-col gap-2">
                  <Text size="xs" weight="semibold" variant="tertiary">
                    Start from a Gong flow
                  </Text>
                  <Button variant="secondary" size="sm" onClick={() => setGongBrowseOpen(true)}>
                    Browse Gong flows
                  </Button>
                  {gongBrowseOpen && (
                    <LauncherGongFlowBrowse
                      onSelect={handleBrowseGongFlowPick}
                      onClose={() => setGongBrowseOpen(false)}
                      backLabel="← Change sequence"
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                aria-label="Create a new Sequence"
                className="flex items-start gap-2 rounded-lg border border-border-secondary p-3 text-left hover:bg-bg-secondary-hover"
                onClick={() => {
                  // Always cleared here — this is the PLAIN doorway, never
                  // pre-filled from a Gong flow pick (`gongCreatePrefillFlow`
                  // is only ever set by `handleBrowseGongFlowPick`).
                  setGongCreatePrefillFlow(undefined);
                  setIsCreatingSequence(true);
                }}
              >
                <Icon type={Sparkle} className="mt-0.5 shrink-0 text-content-tertiary" />
                <span className="flex flex-col gap-0.5">
                  <Text size="sm" weight="medium">
                    Create a new Sequence
                  </Text>
                  <Text size="xs" variant="tertiary">
                    Registered in the library right away, so it can be reused in any Playbook.
                  </Text>
                </span>
              </button>
              {sequences === undefined ? (
                <Text size="xs" variant="tertiary">
                  Loading sequences…
                </Text>
              ) : sequences.length === 0 ? (
                <Text size="xs" variant="tertiary">
                  No sequences in the library yet — create one above.
                </Text>
              ) : filteredSequences && filteredSequences.length === 0 ? (
                <Text size="xs" variant="tertiary">
                  No sequences match. Change the channel filter or create a new one.
                </Text>
              ) : (
                <RadioGroupField
                  label="Sequence"
                  value={sequenceId}
                  onValueChange={(value) => setSequenceId(value)}
                  options={options}
                  // `RadioGroupField` takes `errorMessage` but no `invalid` (its props
                  // are `FormControlProps` + base-ui's `RadioGroup`, and only the
                  // single-input fields carry `invalid`) — the message is the whole
                  // affordance here.
                  errorMessage={showErrors && sequenceError !== null ? sequenceError : undefined}
                />
              )}
            </>
          )}
        </>
      )}
    </PushDrawer>
  );
}
